import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { FLOOR_PLANS, FLOOR_PLAN_FILTERS } from "../data/site";
import { Reveal, SectionHeading } from "./Reveal";
import { X } from "lucide-react";

const AREA_ROWS = [
  ["Carpet Area", "carpet"],
  ["Balcony Area", "balcony"],
  ["BUA", "bua"],
  ["SBUA", "sbua"],
];

export const FloorPlans = ({ onEnquire }) => {
  const [filter, setFilter] = useState("2 BHK");
  const [planView, setPlanView] = useState(null);
  const shown = FLOOR_PLANS.filter((p) => p.category === filter);

  return (
    <section id="floor-plans" data-testid="floor-plans-section" className="py-20 sm:py-28 bg-[#F4FAE8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading index="05" eyebrow="Residences & Layouts" title="2 & 3 BHK Homes at Vinayak 21 Acres" center />
        <Reveal delay={0.1}>
          <p className="text-center text-[#4D7C0F] text-sm sm:text-base leading-relaxed max-w-2xl mx-auto mt-6">
            Choose from efficient 2 BHK 2T homes (approx. 595–597 sq ft carpet) or 3 BHK Smart, Comfort and Luxury
            layouts (approx. 698–943 sq ft carpet) across five towers. Browse the{" "}
            <Link to="/vinayak-21-acres-floor-plan" className="text-[#65A30D] font-semibold hover:underline">Vinayak 21 Acres floor plans</Link>{" "}
            for every official drawing, or filter the verified configurations below.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="flex flex-wrap justify-center gap-2.5 mt-10" data-testid="floorplan-filters">
            {FLOOR_PLAN_FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`floorplan-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`text-[13px] font-sub font-medium px-5 py-2.5 rounded-full border transition-all duration-300 active:scale-[0.97] ${
                  filter === f
                    ? "bg-[#84CC16] text-[#365314] border-[#84CC16]"
                    : "bg-white text-[#4D7C0F] border-[#DCECC0] hover:border-[#84CC16]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </Reveal>

        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 max-w-7xl mx-auto"
          >
            {shown.map((p) => (
              <div
                key={p.slug}
                data-testid={`floorplan-card-${p.slug}`}
                className="bg-white border border-[#DCECC0] rounded-2xl p-7 hover:border-[#84CC16] hover:shadow-[0_24px_48px_-20px_rgba(77,124,15,0.22)] transition-all duration-500"
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-serif-display text-2xl font-medium text-[#365314]">{p.config}</h3>
                  <span className="font-mono-spec text-[10px] tracking-[0.2em] uppercase text-[#84CC16]">{p.tower}</span>
                </div>
                <div className="mt-6 divide-y divide-[#E3EFCB]">
                  {AREA_ROWS.map(([label, key]) => (
                    <div key={key} className="flex items-center justify-between py-2.5">
                      <span className="text-[13px] text-[#65A30D]">{label}</span>
                      <span className="font-mono-spec text-[13px] text-[#365314]">{p[key]}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between py-2.5">
                    <span className="text-[13px] text-[#65A30D]">Tower / Type</span>
                    <span className="text-[12px] text-[#365314] text-right">{p.tower}</span>
                  </div>
                </div>
                <p className="text-[11px] text-[#65A30D] leading-relaxed mt-4">{p.note}</p>
                <div className="flex gap-2.5 mt-6">
                  <button
                    data-testid={`floorplan-view-${p.slug}`}
                    onClick={() => setPlanView(p)}
                    className="flex-1 text-[13px] font-sub font-medium border border-[#9CCB58] text-[#365314] rounded-full px-4 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300"
                  >
                    View Floor Plan
                  </button>
                  <button
                    data-testid={`floorplan-enquire-${p.slug}`}
                    onClick={() => onEnquire(`${p.config} · ${p.tower}`, "Availability Enquiry")}
                    className="flex-1 text-[13px] font-sub font-medium bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] rounded-full px-4 py-3 hover:brightness-105 active:scale-[0.98] transition-all duration-300"
                  >
                    Get Latest Availability
                  </button>
                </div>
              </div>
            ))}
          </motion.div>
        </AnimatePresence>

        <Reveal delay={0.1}>
          <p className="text-center text-xs text-[#65A30D] mt-10 max-w-xl mx-auto leading-relaxed">
            Official floor plans from the project brochure — every tower and type across Towers 1A, 1B, 2A, 2B and
            2C. Areas are approximate and vary by floor; contact Forest Hill Realtors for the latest availability.
          </p>
          <div className="text-center mt-5 flex flex-wrap justify-center gap-3">
            <Link
              to="/vinayak-21-acres-floor-plan"
              data-testid="floorplan-view-all-link"
              className="inline-flex items-center gap-2 text-[#365314] text-sm font-sub font-semibold border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300"
            >
              View All Floor Plans
            </Link>
            <Link
              to="/2-bhk-flats-new-town-kolkata"
              data-testid="floorplan-2bhk-guide-link"
              className="inline-flex items-center gap-2 text-[#365314] text-sm font-sub font-semibold border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300"
            >
              Explore 2 BHK Flats in New Town
            </Link>
            <Link
              to="/3-bhk-flats-new-town-kolkata"
              data-testid="floorplan-3bhk-guide-link"
              className="inline-flex items-center gap-2 text-[#365314] text-sm font-sub font-semibold border border-[#9CCB58] rounded-full px-6 py-3 hover:bg-[#EAF5D6] hover:text-[#3F6212] active:scale-[0.98] transition-all duration-300"
            >
              Explore 3 BHK Flats in New Town
            </Link>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {planView && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#F4FAE8]/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-8"
            onClick={() => setPlanView(null)} data-testid="plan-viewer-overlay"
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl max-w-4xl w-full max-h-[92svh] overflow-y-auto p-4 sm:p-6"
              data-testid="plan-viewer-modal"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="overline-tag">{planView.tower}</p>
                  <h3 className="font-serif-display text-2xl text-[#365314] mt-2">{planView.config}</h3>
                </div>
                <button data-testid="plan-viewer-close-btn" onClick={() => setPlanView(null)} className="p-2 text-[#65A30D] hover:text-[#365314] transition-colors" aria-label="Close">
                  <X size={22} />
                </button>
              </div>
              <img src={planView.planImage} alt={`Official floor plan — ${planView.config}, Vinayak 21 Acres`} className="w-full h-auto rounded-lg border border-[#DCECC0]" />
              <p className="text-[11px] text-[#65A30D] mt-3 leading-relaxed">
                {planView.note} Plan from the official project brochure; subject to change — verify with Forest Hill Realtors.
              </p>
              <button
                data-testid="plan-viewer-enquire-btn"
                onClick={() => { const c = `${planView.config} · ${planView.tower}`; setPlanView(null); onEnquire(c, "Floor Plan Enquiry"); }}
                className="mt-5 w-full bg-gradient-to-r from-[#84CC16] to-[#65A30D] text-[#365314] font-sub font-bold text-sm px-6 py-4 rounded-full hover:brightness-105 active:scale-[0.98] transition-all duration-300"
              >
                Enquire About This Plan
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
